/// <reference path="cookies.ts" />

// Supported languages
const enum Language {
    NL = "nl",
    FR = "fr",
    EN = "en",
}

// Supported languages
const LANGUAGES = [Language.NL, Language.FR, Language.EN];

// Current language
let LANG: Language = Language.NL;

// isSupportedLang returns true if `lang` is a supported language.
function isSupportedLang(lang: string) {
    return (LANGUAGES as string[]).indexOf(lang) !== -1;
}

// _getDefaultLang returns the default language to use for the interface, based
// on whatever cookies or browser preferences are set.
function _getDefaultLang(): string {
    // 1. Cookie
    const cookieLang = getCookie("lang");
    if (cookieLang) return cookieLang;
    // 2. Browser preferences
    // extractLang extracts a language from a locale such as "nl-BE", "en-us",
    // "FR_FR", or "en" (respectively "nl", "en", "fr", and "en").
    const extractLang = (locale: string): string => {
        if (!locale) return "";
        if (locale.indexOf("-") !== -1) locale = locale.split("-")[0];
        if (locale.indexOf("_") !== -1) locale = locale.split("_")[0];
        return locale.toLowerCase();
    };
    // * navigator.languages returns an array of the user's preferred languages
    //   as set in the browser settings. This is the best option to use, but
    //   only supported by newer browsers.
    // * navigator.language is a widely supported setting that returns either
    //   the user's preferred language (most browsers) or the language of the
    //   browser UI (Chrome)
    // * navigator.userLanguage is IE-specific and returns the user's preferred
    //   language (not necessarily that of the browser UI)
    // Sources:
    // * https://zzz.buzz/2016/01/13/detect-browser-language-in-javascript/
    // * https://developer.mozilla.org/en-US/docs/Web/API/NavigatorLanguage/languages
    // * https://developer.mozilla.org/en-US/docs/Web/API/NavigatorLanguage/language
    // * https://stackoverflow.com/a/25603630/8137
    if (navigator.languages) {
        // Find first supported language
        const langs = navigator.languages.map(extractLang);
        const pref = _findInArray(langs, isSupportedLang);
        if (pref !== undefined) return pref;
    }
    if ("language" in navigator && isSupportedLang(navigator.language))
        return navigator.language;
    if (
        "userLanguage" in navigator &&
        isSupportedLang((navigator as any)["userLanguage"])
    )
        return (navigator as any)["userLanguage"];
    // 3. Host name
    if (window.location.hostname === "meteo.brussels") {
        return "fr";
    } else {
        // weer.brussels
        return "nl";
    }
}

// setLang sets the language of the GUI to `langStr`.
// If `langStr` is not given, sets the language to the default language.
function setLang(langStr?: string): void {
    if (!langStr) langStr = _getDefaultLang();

    if (isSupportedLang(langStr)) LANG = langStr as Language;
    else LANG = Language.EN;

    setCookie("lang", LANG);

    // Update translatable texts
    d3.selectAll("[data-text]").html(function (_d, _i) {
        const el = d3.select(this);
        const key = el.attr("data-text");
        return text(key as LocText);
    });

    // Update <html>'s lang attribute
    d3.select("html").attr("lang", LANG);

    // Set datetime locales
    d3.timeFormatDefaultLocale(D3_LOCALES[LANG]);
    TIME_FORMAT = d3.timeFormat(text("time_format"));

    // Update language selector
    d3.selectAll("#lang-selector > li").classed("active", false);
    d3.selectAll("#lang-selector > li[data-val='" + LANG + "']").classed(
        "active",
        true,
    );
}

// switchLang set the language of the GUI to `lang`, and updates it.
function switchLang(lang: string) {
    setLang(lang);
    updateUI();
}

// A piece of translated text
interface TranslatedText {
    nl: string;
    fr: string;
    en: string;
}

// Names of the localized text fragments
type LocText =
    | "title"
    | "head"
    | "last_update"
    | "now"
    | "forecast"
    | "temp_title"
    | "precip_title"
    | "precip_label1"
    | "precip_label2"
    | "time_format"
    | "info"
    | "info_text";

// text returns the text fragment with `key` in the current language.
function text(key: LocText) {
    if (key in TEXT[LANG]) return TEXT[LANG][key as LocText];
    else return "";
}

// Translated text fragments
const TEXT: { [L in Language]: { [T in LocText]: string } } = {
    nl: {
        title: "Het weer in Brussel",
        head: "Het weer in Brussel",
        last_update: "om %-H:%M",
        now: "Nu",
        forecast: "Voorspelling",
        temp_title: "Temperatuur (\u00B0C)",
        precip_title: "Neerslag (mm/u)",
        precip_label1: "{}% kans",
        precip_label2: "op {} mm",
        time_format: "%a %-Hu", // e.g. ma 8u
        info: "Info",
        info_text:
            "<p>Deze website toont de weerswaarnemingen op dit moment in Brussel (Belgi&euml;). We tonen ook de weersverwachtingen voor de komende drie dagen: temperatuur, neerslag (regen, sneeuw, hagel) en bewolking.</p>" +
            '<p>Heb je vragen, opmerkingen of feedback? Mail deze dan naar <a href="mailto:info@weer.brussels">info@weer.brussels</a>.</p>' +
            '<p>Naast deze website maakte ik ook <a href="https://nuopderadio.be/">nu op de radio, met de playlists van de VRT radiostations</a>.</p>',
    },
    fr: {
        title: "Le météo à Bruxelles",
        head: "Le météo à Bruxelles",
        last_update: "à %-H:%M",
        now: "Maintenant",
        forecast: "Prévisions",
        temp_title: "Température (\u00B0C)",
        precip_title: "Précipitations (mm/h)",
        precip_label1: "{}% probabilité",
        precip_label2: "de {} mm",
        time_format: "%a %-Hh",
        info: "Info",
        info_text:
            "<p>Ce site web affiche les observations météorologiques actuelles pour Bruxelles (en Belgique). On présente aussi les prévisions pour les trois prochains jours, comprenant la température, les précipitations (pluie, neige, grêle) et la couverture nuageuse.</p>" +
            '<p>Si vous avez des questions ou des remarques, nous vous invitons à nous les envoyer par courriel à <a href="mailto:info@weer.brussels">info@weer.brussels</a>.</p>' +
            '<p>N&rsquo;hésitez pas à visiter notre autre site web, <a href="https://nuopderadio.be/">nu op de radio</a>, listant les playlists des stations de radio du VRT.</p>',
    },
    en: {
        title: "The weather in Brussels",
        head: "The weather in Brussels",
        last_update: "at %-H:%M",
        now: "Now",
        forecast: "Forecast",
        temp_title: "Temperature (\u00B0C)",
        precip_title: "Precipitation (mm/h)",
        precip_label1: "{}% chance",
        precip_label2: "of {} mm",
        time_format: "%a %-Hh",
        info: "Info",
        info_text:
            "<p>This website shows the current weather observations and forecasts for Brussels, Belgium, consisting of temperature, precipitation (rain, snow, hail), and cloud coverage for the next three days.</p>" +
            '<p>If you have questions, comments, or feedback, you can mail us at <a href="mailto:info@weer.brussels">info@weer.brussels</a>.</p>' +
            '<p>I also made a website with the <a href="https://nuopderadio.be/">playlist of the radio stations of VRT</a>, the Flemish public broadcaster.</p>',
    },
};

const CONDITIONS: { [L in Language]: { [C in CONDITION]: string } } = {
    nl: {
        "clear-day": "zonnig",
        "clear-night": "heldere nacht",
        cloudy: "bewolkt",
        "fog-day": "mistig",
        "fog-night": "mistig",
        fog: "mistig",
        "heavy-rain": "zware regen",
        "heavy-snow": "sneeuw",
        "light-rain": "regenachtig",
        "light-snow": "sneeuwachtig",
        "lightning-rain-day": "onweer",
        "lightning-rain-night": "onweer",
        "lightning-rain": "onweer",
        "partly-cloudy-day": "gedeeltelijk bewolkt",
        "partly-cloudy-night": "gedeeltelijk bewolkt",
        "quite-clear-day": "zonnig met enkele wolken",
        "quite-clear-night": "enkele wolken",
        "rain-day": "regen",
        "rain-night": "regen",
        rain: "regen",
        "snow-day": "sneeuw",
        "snow-night": "sneeuw",
        "snow-rain-day": "sneeuw en regen",
        "snow-rain-night": "sneeuw en regen",
        "snow-rain": "sneeuw en regen",
    },
    fr: {
        "clear-day": "soleil",
        "clear-night": "dégagé",
        cloudy: "nuageux",
        "fog-day": "brouillard",
        "fog-night": "brouillard",
        fog: "brouillard",
        "heavy-rain": "pluie intense",
        "heavy-snow": "neige intense",
        "light-rain": "pluvieux",
        "light-snow": "neigeux",
        "lightning-rain-day": "orage",
        "lightning-rain-night": "orage",
        "lightning-rain": "orage",
        "partly-cloudy-day": "partiellement nuageux",
        "partly-cloudy-night": "partiellement nuageux",
        "quite-clear-day": "éclaircies",
        "quite-clear-night": "légèrement nuageux",
        "rain-day": "pluie",
        "rain-night": "pluie",
        rain: "pluie",
        "snow-day": "neige",
        "snow-night": "neige",
        "snow-rain-day": "neige et pluie",
        "snow-rain-night": "neige et pluie",
        "snow-rain": "neige et pluie",
    },
    en: {
        "clear-day": "sunny",
        "clear-night": "clear night",
        cloudy: "cloudy",
        "fog-day": "foggy",
        "fog-night": "foggy",
        fog: "foggy",
        "heavy-rain": "heavy rain",
        "heavy-snow": "heavy snow",
        "light-rain": "rainy",
        "light-snow": "snowy",
        "lightning-rain-day": "thunderstorm",
        "lightning-rain-night": "thunderstorm",
        "lightning-rain": "thunderstorm",
        "partly-cloudy-day": "partly cloudy",
        "partly-cloudy-night": "partly cloudy",
        "quite-clear-day": "sunny with some clouds",
        "quite-clear-night": "some clouds",
        "rain-day": "rain",
        "rain-night": "rain",
        rain: "rain",
        "snow-day": "snow",
        "snow-night": "snow",
        "snow-rain-day": "snow and rain",
        "snow-rain-night": "snow and rain",
        "snow-rain": "snow and rain",
    },
};

// conditionText returns the localized text for a weather condition.
function conditionText(condition: string, lang: Language): string {
    return Object.values(CONDITION).includes(condition as CONDITION)
        ? CONDITIONS[lang][condition as CONDITION]
        : condition;
}

// D3 locale info
// Copied (and slightly modified) from d3.js
// (https://github.com/d3/d3-time-format/blob/master/locale/)
// prettier-ignore
const D3_LOCALES: {[L in Language]: d3.TimeLocaleDefinition} = {
    "nl": {
        "dateTime": "%a %e %B %Y %T",
        "date": "%d-%m-%Y",
        "time": "%H:%M:%S",
        "periods": ["AM", "PM"],
        "days": ["zondag", "maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag"],
        "shortDays": ["zo", "ma", "di", "wo", "do", "vr", "za"],
        "months": ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"],
        "shortMonths": ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"]
    },
    "fr": {
        "dateTime": "%A, le %e %B %Y, %X",
        "date": "%d/%m/%Y",
        "time": "%H:%M:%S",
        "periods": ["AM", "PM"],
        "days": ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"],
        "shortDays": ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"],
        "months": ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"],
        "shortMonths": ["janv", "févr", "mars", "avr", "mai", "juin", "juil", "août", "sept", "oct", "nov", "déc"]
    },
    "en": { // en-GB
        "dateTime": "%a %e %b %X %Y",
        "date": "%d/%m/%Y",
        "time": "%H:%M:%S",
        "periods": ["AM", "PM"],
        "days": ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "shortDays": ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
        "months": ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
        "shortMonths": ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    }
};

// Time format
// This is updated after the locale has been set.
let TIME_FORMAT: (date: Date) => string = d3.timeFormat("%a %-Hu");
